import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import ScrollToTop from '@/components/base/ScrollToTop'
import Breadcrumb from '@/components/base/Breadcrumb'
import { categories, Category } from '@/mocks/categories'
import { getArticlesByCategory, Article } from '@/mocks/articles'

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>()
  const [category, setCategory] = useState<Category | undefined>(undefined)
  const [articles, setArticles] = useState<Article[]>([])
  const [activeSlug, setActiveSlug] = useState(slug || 'hot')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  useEffect(() => {
    const found = categories.find((c) => c.slug === activeSlug)
    setCategory(found)
    setArticles(getArticlesByCategory(activeSlug))
  }, [activeSlug])

  const handleCategorySwitch = (newSlug: string) => {
    setActiveSlug(newSlug)
    window.scrollTo(0, 0)
  }

  const pageTitle = category ? `${category.name} - 色里番分类` : '分类浏览'
  const pageDesc = category?.description || '按分类浏览色里番作品'

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://tomleeled.cn/category/${activeSlug}`,
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '色里番精选推荐', url: 'https://tomleeled.cn/' },
  }

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类' },
            ]}
          />

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {category?.name || '分类浏览'}
            </h1>
            <p className="text-foreground-500 max-w-2xl leading-relaxed">
              {category?.description || ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10 pb-6 border-b border-background-200">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategorySwitch(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        {article.rating}
                      </span>
                      <span>{article.views.toLocaleString()} 浏览</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  )
}